import React from 'react'
import { BOOKMAKER_LOGOS } from '../config/bookmakers'
import { eventLabel } from '../config/sports'
import { MARKET_TYPE_MAPPING, SELECTION_SHORT_NAMES } from '../config/markets'
import { TourMarker } from '../components/ToolTour'
import '../styles/app-mobile.css'
import '../ARB_NEW_PAGE/ArbNew.css'
import './BonusNew.css'

// One bonus conversion as a mobile card. Used by BonusNewMobile for every row
// and by the Bonus Converter tour (on desktop too), so the tour always shows
// the real card. Odds, stakes and retention arrive already worked out (they
// follow the user's calculator edits and odds format); the card lays them out.

const formatMarketType = (mt) => {
  if (MARKET_TYPE_MAPPING[mt]) return MARKET_TYPE_MAPPING[mt]
  return (mt || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

const formatDateTime = (ts) =>
  new Date(ts + 'Z').toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  })

export const formatSelection = (selection, marketType, playerName = '') => {
  if (SELECTION_SHORT_NAMES[marketType]) {
    const parts = selection.split(' ')
    for (let i = 0; i < parts.length; i++) {
      if ((parts[i].toLowerCase() === 'over' || parts[i].toLowerCase() === 'under') && i + 1 < parts.length && parts[i + 1].replace('.', '').replace('-', '').match(/^\d+$/)) {
        return playerName
          ? `${playerName} ${parts[i]} ${parts[i + 1]} ${SELECTION_SHORT_NAMES[marketType]}`
          : `${parts[i]} ${parts[i + 1]} ${SELECTION_SHORT_NAMES[marketType]}`
      }
    }
  }
  if (marketType === 'alternate_lines') {
    const parts = selection.split(' ')
    if (parts.length >= 2) {
      const team = parts.slice(0, -1).join(' ')
      let hcap = parts[parts.length - 1]
      if (hcap[0] !== '-' && hcap[0] !== '+') hcap = `+${hcap}`
      return `${team} ${hcap}`
    }
  }
  return selection
}

export const retentionBadgeClass = (r) => {
  if (r >= 80) return 'bc-ev-badge retention-badge-gold'
  if (r >= 70) return 'bc-ev-badge retention-badge-green'
  if (r >= 60) return 'bc-ev-badge retention-badge-blue'
  return 'bc-ev-badge retention-badge-red'
}

const noop = () => {}

/**
 * oddsText: one entry per outcome, already formatted
 * bonusStake / hedgeStake: the stakes for the BONUS and HEDGE outcomes
 * markers: tour only, { market, bonus, hedge, odds, stakes, retention, track } -> number;
 *   odds and stakes pin to the first outcome row
 * children: rendered at the bottom of the card (the expanded calculator)
 */
export default function BonusCard({
  opp, oddsText, bonusStake, hedgeStake, retention, className = '', style, blurStats = false,
  isExpanded = false, tracked = false, showButtons = true,
  onOpenBook = noop, onExpand = noop, onTrack = noop, onHide = noop,
  markers = {}, children,
}) {
  const mark = (part, style) => markers[part] != null && <TourMarker n={markers[part]} style={style} />
  const anchor = (part, oi) => (oi === 0 && markers[part] != null ? { position: 'relative' } : null)
  // First card in preview: blur the actionable stats (book, selection, odds, stake)
  // as a teaser, matching the EV mobile page.
  const previewStatStyle = blurStats ? { filter: 'blur(5px)', userSelect: 'none' } : {}

  return (
    <div className={`bcm-card${className ? ` ${className}` : ''}`} style={style}>

      {/* Market type strip (it clips its own overflow, so a marker sits beside the text inside it) */}
      <div className="bcm-card-type-strip">
        {markers.market != null ? (
          <span style={{ position: 'relative' }}>
            {formatMarketType(opp.market_type)}
            {mark('market', { top: '-4px', right: '-28px' })}
          </span>
        ) : formatMarketType(opp.market_type)}
      </div>

      {/* Retention badge — top right */}
      <div className={`bcm-ev-corner ${retentionBadgeClass(retention)}`}>
        {retention.toFixed(1)}%
        {mark('retention', { top: '-8px', right: '-6px' })}
      </div>

      {/* Match + time */}
      <div className="bcm-card-top" style={{ paddingRight: '60px' }}>
        <div className="bcm-card-name-block">
          <div className="bcm-card-match">{opp.match}</div>
          <div className="bcm-card-meta-row">
            <span className="bc-sport-badge">{eventLabel(opp)}</span>
            <span className="bcm-card-time">{formatDateTime(opp.commence_time)}</span>
          </div>
        </div>
      </div>

      {/* Two outcome rows */}
      <div className="arb-mobile-outcomes">
        {opp.outcomes?.map((outcome, oi) => {
          const logo    = BOOKMAKER_LOGOS[outcome.bookmaker]
          const isBonus = outcome.bet_type === 'BONUS'
          const stake   = isBonus ? bonusStake : hedgeStake
          return (
            <div key={oi} className="bonus-outcome-row" style={{
              background: isBonus ? 'rgba(245, 158, 11, 0.05)' : 'rgba(100, 116, 139, 0.05)',
              borderColor: isBonus ? 'rgba(245, 158, 11, 0.25)' : 'rgba(100, 116, 139, 0.2)',
            }}>
              {/* Corner banner */}
              <div style={{
                position: 'absolute', top: 0, left: 0,
                background: isBonus ? 'rgba(245, 158, 11, 0.75)' : 'rgba(100, 116, 139, 0.35)',
                color: isBonus ? '#1c1000' : '#cbd5e1',
                fontSize: '7px', fontWeight: '700', letterSpacing: '0.06em',
                textTransform: 'uppercase',
                padding: '1px 5px',
                borderBottomRightRadius: '4px',
                lineHeight: 1.4,
              }}>
                {isBonus ? 'Bonus' : 'Hedge'}
                {mark(isBonus ? 'bonus' : 'hedge', { top: '-6px', right: '-24px' })}
              </div>
              <div className="bonus-outcome-left">
                {logo
                  ? <img src={logo} alt={outcome.bookmaker} className="arb-book-logo" style={previewStatStyle} onClick={() => onOpenBook(outcome)} />
                  : <div className="arb-book-abbr" style={previewStatStyle} onClick={() => onOpenBook(outcome)}>{outcome.bookmaker.slice(0, 2).toUpperCase()}</div>
                }
                <span className="arb-outcome-sel" style={previewStatStyle}>{formatSelection(outcome.selection, opp.market_type, opp.player_name)}</span>
              </div>
              <div className="bonus-outcome-right">
                <span className="bonus-outcome-odds" style={{ ...previewStatStyle, ...anchor('odds', oi) }}>
                  {oddsText[oi]}
                  {oi === 0 && mark('odds', { top: '-17px', right: '-6px' })}
                </span>
                <span className="bonus-outcome-stake" style={{ ...previewStatStyle, ...anchor('stakes', oi) }}>
                  ${stake}
                  {oi === 0 && mark('stakes', { top: '-19px', right: '-10px' })}
                </span>
              </div>
            </div>
          )
        })}
      </div>

      {/* Button strip */}
      {showButtons && (
        <div className="bcm-btn-strip">
          <div style={{ flex: 1 }} />
          <div className="bcm-strip-right">
            <button
              className={`bcm-strip-btn${isExpanded ? ' active' : ''}`}
              onClick={onExpand}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                {isExpanded ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
              </svg>
            </button>
            <button
              className={`bcm-strip-btn${tracked ? ' tracked' : ''}`}
              onClick={onTrack}
              style={markers.track != null ? { position: 'relative' } : undefined}
            >
              {tracked
                ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              }
              {mark('track')}
            </button>
            <button className="bcm-strip-btn hide-btn" onClick={onHide}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                <line x1="1" y1="1" x2="23" y2="23"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      {children}
    </div>
  )
}
